/**
 * Sprint 61 — En qué cantidades se puede usar un insumo.
 *
 * Reporte de campo: *"tienes 48.5 piezas de Coca Cola Clásica… ¿compré media
 * lata?"*. El sistema trataba `pieza` como una sola cosa y aplicaba porcentajes de
 * merma sobre ella: el 5 % de 30 latas daba 1.5, y el inventario quedaba en medias
 * latas.
 *
 * Pero el arreglo no es "las piezas son enteras". El propio negocio lo aclaró: el
 * pollo se vende **entero o en mitad** — y mañana quizá en cuartos. Y una botella
 * de licor por copeo es una pieza que se sirve en veinte partes, con merma real
 * (derrames, el fondo que no se sirve). Así que la divisibilidad es un dato **del
 * insumo**, no de su unidad de medida, y la decide el negocio.
 *
 * Consecuencia sobre la merma, que es la otra mitad del reporte: en algo
 * indivisible que se entrega cerrado, la **merma de cocción no existe** — una lata
 * cruda es una lata cocida. Se puede romper una lata (eso es merma de manejo, y se
 * pierde **una**, no 1.5), pero no encoge al cocinarse.
 */

import { esUnidadContinua } from './medidas';

/** Redondeos posibles. El modo importa: comprar de menos deja al negocio sin producto. */
export type ModoRedondeo = 'cerca' | 'arriba' | 'abajo';

/** Tolerancia para comparaciones: los flotantes arrastran 0.30000000000000004. */
const EPSILON = 1e-9;

/**
 * Ajusta una cantidad al múltiplo más próximo de `fraccion`.
 *
 * `fraccion` nula o no positiva significa "cualquier cantidad" (kilos, litros): la
 * cantidad se devuelve **intacta**. Nada de sanear decimales "por si acaso" — un
 * test lo cazó: recortar 0.4166666… a 0.416667 hace que 10 kg rindan 23 porciones
 * en vez de 24, porque el resultado se divide y se trunca después. En la ruta
 * continua esta función no debe cambiar ningún valor.
 */
export function redondearAFraccion(
  cantidad: number,
  fraccion: number | null | undefined,
  modo: ModoRedondeo = 'cerca',
): number {
  if (!Number.isFinite(cantidad)) return 0;
  if (!fraccion || fraccion <= 0) return cantidad;

  const pasos = cantidad / fraccion;
  const redondeados =
    modo === 'arriba'
      ? Math.ceil(pasos - EPSILON)
      : modo === 'abajo'
        ? Math.floor(pasos + EPSILON)
        : Math.round(pasos);

  return limpiar(redondeados * fraccion);
}

/** ¿Esta cantidad es un múltiplo válido de la fracción? */
export function esMultiploDeFraccion(
  cantidad: number,
  fraccion: number | null | undefined,
): boolean {
  if (!fraccion || fraccion <= 0) return true;
  const resto = Math.abs(cantidad / fraccion - Math.round(cantidad / fraccion));
  return resto < 1e-6;
}

/**
 * ¿Este insumo se entrega siempre completo?
 *
 * Es el caso donde la merma de cocción no tiene sentido físico: la lata que se
 * sirve cerrada no encoge. Un insumo con fracción 0.5 o 0.05 **sí** se manipula
 * (se parte el pollo, se sirve la copa) y ahí la merma es real.
 */
export function esIndivisible(fraccion: number | null | undefined): boolean {
  return typeof fraccion === 'number' && Math.abs(fraccion - 1) < EPSILON;
}

/**
 * Fracción sugerida para un insumo que todavía no la tiene definida.
 *
 * Sólo se usa como valor inicial al migrar y al crear: lo vendido por peso o
 * volumen admite cualquier cantidad, y lo contado por piezas se asume completo
 * hasta que el negocio diga lo contrario. Asumir "completo" es el default seguro —
 * el error que produce (no dejar registrar media lata) es visible y se corrige en
 * un campo; el contrario deja inventario en fracciones imposibles sin avisar.
 */
export function fraccionSugeridaPorUnidad(unidad: string): number | null {
  return esUnidadContinua(unidad) ? null : 1;
}

/**
 * Cuánto insumo consume de verdad una venta que "necesita" `cantidad`.
 *
 * Sólo redondea —hacia arriba— cuando el insumo **se entrega completo**: para servir
 * algo que necesita 0.3 de una lata hay que abrir una lata, y descontar 0.3 dejaría
 * el inventario en fracciones que no existen. Es el caso del reporte.
 *
 * En un insumo que **sí** se parte (el pollo en mitades, la botella por copeo) la
 * cantidad se deja exacta a propósito. Redondear cada venta hacia arriba ahí
 * inventaría una merma que el negocio no tuvo: media porción con 20 % de merma
 * consume 0.625, no un pollo entero. La fracción acota lo que una persona compra o
 * cuenta, no lo que la cocción se lleva.
 */
export function redondearConsumo(cantidad: number, fraccion: number | null | undefined): number {
  if (!esIndivisible(fraccion)) return cantidad;
  return redondearAFraccion(cantidad, fraccion, 'arriba');
}

/**
 * "Unidades completas" dicho en la unidad real y bien escrito.
 *
 * Las unidades son un conjunto cerrado, así que se resuelven a mano: género y plural
 * en español no salen de concatenar una "s". Frases como "se maneja en pieza
 * completas" restan credibilidad justo cuando el sistema está corrigiendo a alguien.
 */
const COMPLETAS: Record<string, string> = {
  pieza: 'piezas completas',
  porcion: 'porciones completas',
  litro: 'litros completos',
  kg: 'kilos completos',
};

/** Cómo se le explica al operador en qué cantidades puede mover este insumo. */
export function describirFraccion(fraccion: number | null | undefined, unidad: string): string {
  if (!fraccion || fraccion <= 0) return `cualquier cantidad de ${unidad}`;
  if (esIndivisible(fraccion)) {
    return COMPLETAS[unidad.trim().toLowerCase()] ?? `${unidad} completas`;
  }
  const partes = Math.round(1 / fraccion);
  if (Math.abs(1 / fraccion - partes) < 1e-6) {
    if (partes === 2) return `mitades de ${unidad}`;
    if (partes === 4) return `cuartos de ${unidad}`;
    return `${partes} partes por ${unidad}`;
  }
  return `múltiplos de ${fraccion} ${unidad}`;
}

/**
 * Sprint 109 — "¿Cómo puedo vender medio pollo?"
 *
 * Pregunta del negocio, y la respuesta era que **sí se podía por dentro y no por fuera**: el modelo
 * lo soporta desde el Sprint 61 —el comentario de arriba dice literalmente *"el pollo se vende
 * entero o en mitad"*— pero las dos pantallas del catálogo tenían la divisibilidad clavada:
 *
 * ```ts
 * fraccionMinima: unidad === 'pieza' ? 1 : 0.25
 * ```
 *
 * Un pollo se captura como pieza, nace indivisible, y ya nunca acepta un tamaño de 0.5. La
 * capacidad existía y no había dónde pedirla.
 *
 * ## Por qué esto y no una unidad `{ENTERO | MITAD}`
 *
 * Porque una unidad de medida que se parte deja de servir para contar. ¿Un pollo son dos mitades?
 * ¿Cuántas "mitades" hay en el almacén cuando quedan tres pollos y medio? Cada venta obligaría a
 * convertir a mano, que es donde el inventario se descuadra sin que nadie lo note. Con la fracción,
 * medio pollo descuenta **0.5 piezas** y el conteo sigue cerrando solo.
 */
/**
 * Sprint 127 — En cuánto se parte algo, **deducido de lo que el negocio vende**.
 *
 * Esto reemplaza a la pregunta *"¿se vende partido?"* del Sprint 109 y al default `0.25` que
 * rechazaba los 125 g de costilla. El motivo es de oficio, no de código:
 *
 * > Una dueña no decide de antemano en cuántas partes vende. Lo **descubre vendiendo**. Primero
 * > son cuatro tamaños fijos, luego llega alguien que quiere 125 g, y después un paquete de kilo
 * > y medio. Si cada descubrimiento exige entrar a una pantalla de configuración a darle permiso
 * > al sistema, el sistema estorba.
 *
 * Con la fracción deducida, agregar un renglón a la tabla de tamaños **es** darle el permiso. Un
 * "1/4 de pollo" nuevo baja la divisibilidad a 0.25 sin que nadie tenga que saber que existe algo
 * llamado divisibilidad.
 *
 * Reglas:
 * - Lo que se pesa o se mide admite **cualquier cantidad** (`null`). No hay rejilla que valga en
 *   un kilo: es la regla del Sprint 61, que el 109 rompió sin querer.
 * - Lo que se cuenta toma el **máximo común divisor** de sus tamaños. Sólo enteros → 1, y la lata
 *   sigue siendo indivisible (que es lo que evita el *"tienes 48.5 latas"* del Sprint 61). Con un
 *   "Medio" en la tabla → 0.5. Con un "Un cuarto" → 0.25.
 * - Nunca por encima de 1: vender de dos en dos no vuelve al insumo indivisible de dos en dos.
 *
 * Sin tamaños todavía (la tabla vacía del alta) se asume entero, que es el default seguro de
 * siempre: el error que produce se ve y se corrige en un renglón; el contrario deja el almacén en
 * fracciones imposibles sin avisar.
 */
export function fraccionDeFormatos(cantidades: number[], unidad: string): number | null {
  if (esUnidadContinua(unidad)) return null;

  const validas = cantidades.filter((c) => Number.isFinite(c) && c > 0);
  if (validas.length === 0) return 1;

  // En milésimas y con enteros: el MCD de flotantes es donde 0.1 + 0.2 arruina el resultado.
  let mcd = 0;
  for (const c of validas) mcd = maximoComunDivisor(mcd, Math.round(c * 1000));
  if (mcd <= 0) return 1;

  return Math.min(1, limpiar(mcd / 1000));
}

/**
 * De dos divisibilidades, la que permite más cosas.
 *
 * Sprint 127 — La deducción **nunca sube** la divisibilidad de algo que ya existe, sólo la baja.
 * Un ingrediente puede tener existencias en mitades por ventas viejas aunque hoy no haya ningún
 * tamaño "Medio" en la tabla: pasarlo a "entero" haría que el inventario redondeara ese medio
 * pollo real la próxima vez que se toque. Quedarse permisivo de más no rompe nada —no bloquea
 * ninguna venta ni descuadra ninguna cuenta—; reescribir existencias del cliente, sí.
 *
 * `null` (cualquier cantidad) gana siempre: es la menos restrictiva de todas.
 */
export function fraccionMasFina(
  a: number | null | undefined,
  b: number | null | undefined,
): number | null {
  if (a === null || a === undefined) return null;
  if (b === null || b === undefined) return null;
  return Math.min(a, b);
}

function maximoComunDivisor(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y > 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

/** Quita la basura de coma flotante sin alterar el valor real. */
function limpiar(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}
